import { SearchIcon } from '@heroicons/react/outline';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';

import PageLoadingIndicator from '../../../../components/loading-indicator/page-loading';
import SuccessMessage from '../../../../components/notifications/success';
import ValidationErrors from '../../../../components/notifications/validation-errors';
import PageHeaderWrapper from '../../../../components/page/page-header-wrapper';
import PageTitle from '../../../../components/page/page-title';
import PageWrapper from '../../../../components/page/page-wrapper';
import PageWrapperDataFilter from '../../../../components/page/page-wrapper-data-filter';
import RegularDropdown from '../../../../components/regular-dropdown';
import TableWrapper from '../../../../components/table/table-wrapper';
import withAPI from '../../../../hocs/with-api';
import ModalSetHubEmails from '../../../../components/modal/hubs-set-emails';
import ModalSelectMMRManager from '../../../../components/modal/hubs-mmr-responsible';
import WithPermission from '../../../with-permission';
import NoResults from '../../../../components/notifications/empty-data';
import { setDate } from '../../../../components/dateHandle';
import TableRow from './table-row';
import { DefaultButton } from 'components/buttons';
import TableHeader from '../../components/table-header';
import { scrollToTop } from '../../../../utils';

const routeColumnNames = ['Location'];

class IVMRReport extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      companyId: '',
      setEmailsHubId: '',
      mmrCompanies: [],
      errors: {},
      selectedHubs: new Set(),

      items: [],

      search: '',
      isLoading: false,
      itemCreatedOk: false,

      modalSetEmailsOpen: false,

      currentPage: 1,
    };
    this.tableWrapperRef = React.createRef();
  }

  componentDidMount = async () => {
    document.title = `IVMR Report - ${process.env.REACT_APP_NAME}`;
    this.loadCompanies();
  }

  componentWillUnmount = () => {
    this.handleSearch.cancel();
  }

  handleSearch = _.debounce((query) => {
    this.setState({
      search: query,
    }, this.loadItems);
  }, 500);

  loadCompanies = async () => {
    const { httpRequest } = this.props;
    const response = await httpRequest({
      method: 'get',
      url: 'companies?is_suspended=0',
    }).catch(() => this.setState({ isLoading: false }));
    if (response) {
      const {
        data: {
          data: companies,
        },
      } = response;
      if (_.isArray(companies) && companies.length > 1) {
        this.setState({
          isLoading: false,
          mmrCompanies: companies,
        });
        return null;
      }
      if (companies?.length === 1) {
        this.setState({
          companyId: companies[0].id,
          mmrCompanies: companies,
        }, this.loadItems);
      }
    }
    return null;
  }

  loadItems = async () => {
    const { httpRequest } = this.props;
    const {
      companyId, search, currentPage, perPage,
    } = this.state;

    this.setState({ isLoading: true });
    const perPageQuery = (perPage ? `&per_page=${perPage}` : '');

    const response = await httpRequest({
      method: 'get',
      url: 'reporting/mmr?company_id='
        + `${companyId}`
        + `${currentPage ? `&page=${currentPage}` : ''}`
        + `&search=${search}${perPageQuery}`,
    }).catch((error) => {
      this.setState({
        errors: error?.response?.data || {},
        isLoading: false,
      });
    });
    if (response) {
      const {
        data: {
          data: items,
        },
      } = response;

      this.setState({
        items,
        isLoading: false,
      });
    }
  }

  onOpenDateModal = (open) => {
    if (!open) this.loadItems();
    this.setState({ modalSetDateOpen: open });
  }

  onOpenManagerModal = (open) => {
    if (!open) this.loadItems();
    this.setState({ modalSetMMRManagerOpen: open });
  }

  toggleHubs = async (hub) => {
    const { selectedHubs } = this.state;
    if (selectedHubs.has(hub.id)) {
      selectedHubs.delete(hub.id);
    } else {
      selectedHubs.add(hub.id);
    }
    this.setState({ selectedHubs: new Set(selectedHubs), itemCreatedOk: false });
  }

  onSubmit = async () => {
    const { httpRequest } = this.props;
    const {
      from, to, selectedHubs, companyId,
    } = this.state;

    this.setState({
      itemCreatedOk: false,
      isLoading: true,
    });

    const formatted_values = {};
    if (from instanceof Date) formatted_values.from = setDate(from);
    if (to instanceof Date) formatted_values.to = setDate(to);
    formatted_values.company_hub = Array.from(selectedHubs)
      ?.map((hubId) => ({ hubId, companyId }));

    const posted = await httpRequest({
      method: 'post',
      url: 'reporting/ivmr-report',
      data: formatted_values,
    }).catch((error) => {
      scrollToTop(this.tableWrapperRef);
      this.setState({
        errors: error?.response?.data,
        itemCreatedOk: false,
        isLoading: false,
      });
    });

    if (posted) {
      this.setState({ itemCreatedOk: true, isLoading: false });
    }
  }

  onClickEmails = (id, company_id) => {
    this.setState({
      setEmailsHubId: id,
      modalSetEmailsOpen: true,
      selectedHubCompanyId: company_id,
    });
  }

  onClickManagers = (id, company_id) => {
    this.setState({
      setMMRManagerHubId: id,
      modalSetMMRManagerOpen: true,
      selectedManagerCompanyId: company_id,
    });
  }

  render() {
    const {
      companyId, mmrCompanies, isLoading, items, selectedHubs, errors, itemCreatedOk,
      setEmailsHubId, modalSetEmailsOpen, selectedHubCompanyId,
      setMMRManagerHubId, modalSetMMRManagerOpen, selectedManagerCompanyId,
    } = this.state;
    return (
      <PageWrapper>
        <PageHeaderWrapper>
          <PageTitle title="IVMR Report" />
        </PageHeaderWrapper>
        <PageWrapperDataFilter>
          <div className="relative w-full sm:mr-auto md:w-max">
            <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
              <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              onChange={(e) => { if (companyId) this.handleSearch(e.target.value); }}
              id="search"
              name="search"
              className="md:block flex flex-1 ls:w-max xl:w-max w-full
                        bg-white border border-gray-300 rounded-md pl-10 pr-3
                        text-sm placeholder-gray-500 focus:outline-none focus:text-gray-900
                        focus:placeholder-gray-400 sm:text-sm"
              placeholder="Search"
              type="search"
              disabled={!companyId}
            />
          </div>
          {_.isArray(mmrCompanies) && mmrCompanies.length > 1 && (
            <RegularDropdown
              items={mmrCompanies}
              onChange={(id) => this.setState({
                companyId: id, selectedHubs: new Set(), from: '', to: '', itemCreatedOk: false,
              }, this.loadItems)}
              selectedValue={companyId}
              placeholder="Select company"
              customMargin="ml-0"
              customClass="md:w-max w-full"
            />
          )}
        </PageWrapperDataFilter>
        <TableWrapper ref={this.tableWrapperRef}>
          {!_.isEmpty(errors) && (
            <ValidationErrors errors={errors} addClass="-mt-0" />
          )}
          {
            itemCreatedOk && (
              <SuccessMessage
                message="Updated successfully"
                addClass="-mt-0"
              />
            )
          }
          <PageLoadingIndicator loading={isLoading} />
          {
            companyId && setEmailsHubId && modalSetEmailsOpen && (
              <ModalSetHubEmails
                open={modalSetEmailsOpen}
                setOpen={(opn) => this.setState({ modalSetEmailsOpen: opn }, this.loadItems)}
                setEmailsHubId={setEmailsHubId}
                selectedCompany={{ id: selectedHubCompanyId }}
                managerType="ivmr_managers"
                title="Set responsible emails"
                subtitle="Set responsible emails for location"
              />
            )
          }
          {
            companyId && setMMRManagerHubId && modalSetMMRManagerOpen && (
              <ModalSelectMMRManager
                open={modalSetMMRManagerOpen}
                setOpen={this.onOpenManagerModal}
                hubId={setMMRManagerHubId}
                companyId={selectedManagerCompanyId}
                managerType="ivmr_managers"
                title="Select location IVMR responsible"
                subtitle="Select responsible users for IVMR"
              />
            )
          }

          <table className="min-w-full divide-y divide-gray-200 overflow-x-hidden
                    overflow-y-visible text-left"
          >
            {companyId && !isLoading && items.length !== 0 ? (
              <thead>
                <tr className="text-xs font-medium text-gray-500 tracking-wider">
                  <TableHeader columnList={routeColumnNames} />
                  <WithPermission permissionsCheck={['CompaniesResources']}>
                    <th scope="col" className="px-2 py-3 font-medium">IVMR batch emails recipients</th>
                    <th scope="col" className="px-2 py-3 font-medium">IVMR Responsible Manager</th>
                  </WithPermission>
                </tr>
              </thead>
            ) : null}

            <tbody className={`${isLoading ? 'opacity-50' : ''} bg-white divide-y divide-gray-200`}>

              {items.map((item) => (
                <TableRow
                  row={item}
                  key={`${item.id} ${item?.pivot?.company_id}`}
                  isChecked={selectedHubs.has(item.id)}
                  companyId={companyId}
                  onClickEmails={() => this.onClickEmails(item.id, item?.pivot?.company_id)}
                  onClickManagers={() => this.onClickManagers(item.id, item?.pivot?.company_id)}
                  toggleHubs={this.toggleHubs}
                />
              ))}

              {items.length > 0 && selectedHubs.size > 0 && (
                <tr className="text-sm text-gray-700">
                  <td colSpan="2" className="pt-2">
                    <div className="flex gap-3 items-end">
                      <DefaultButton
                        isLoading={isLoading}
                        onClick={this.onSubmit}
                        label="Send"
                      />
                    </div>
                  </td>
                </tr>
              )}

              {(!companyId)
                ? (
                  <tr>
                    <td colSpan="2" className="pt-2 text-sm">
                      Select company to report
                    </td>
                  </tr>
                )
                : null}
            </tbody>
          </table>
          {companyId && !isLoading && items.length === 0 && (
            <div className="absolute transform left-1/2 top-1/2 -translate-x-1/2 translate-y-1/2
                      w-max"
            >
              <NoResults />
            </div>
          )}
        </TableWrapper>
      </PageWrapper>
    );
  }
}

IVMRReport.propTypes = {
  httpRequest: PropTypes.func.isRequired,
};

export default compose(
  withAPI,
  withRouter,
)(IVMRReport);
